import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { number, object, string } from 'yup';

import { useAlerta, useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, { Botao as BotaoForm, Campo, Selecao } from 'Component/Form';
import Imagem from 'Component/Imagem';
import LayoutDefault from 'Component/Layout';
import Modal from 'Component/Modal';
import Emprestimo from 'Interface/EmprestimosEpi';
import Epi from 'Interface/Epis';
import { Erro } from 'Interface/Requisicao';
import { recuperarMensagemDeErro } from 'View/functions';
import {
  useAdicionarEmprestimoEpiMutation,
  useEditarEmprestimoEpiMutation,
  useRecuperarEmprestimosEpiQuery,
  useRemoverEmprestimoEpiMutation,
} from 'View/Service/Default/EmprestimosEpi';
import {
  useEditarEpiMutation,
  useRecuperarEpiQuery,
} from 'View/Service/Default/Epis';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  nome: string().required('Obrigatório'),
  certificado_de_aprovacao: string().required('Obrigatório'),
});

const regrasEmprestimo = object({
  vinculo_id: number().required('Obrigatório'),
  quantidade: number().required('Obrigatório'),
});

const Editar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const alerta = useAlerta();
  const { id = 0 } = useParams();
  const [modal, setModal] = useState<boolean>(false);
  const [editarEpi] = useEditarEpiMutation();
  const [adicionarEmprestimoEpi] = useAdicionarEmprestimoEpiMutation();
  const [editarEmprestimoEpi] = useEditarEmprestimoEpiMutation();
  const [removerEmprestimoEpi] = useRemoverEmprestimoEpiMutation();

  const { data: { epi } = {}, isLoading } = useRecuperarEpiQuery(id);

  const { data: { emprestimos } = {} } = useRecuperarEmprestimosEpiQuery(
    {
      contain: ['VinculosEmpregaticios.Usuarios'],
      options: { epi_id: epi?.id },
      sort: 'Usuarios.nome',
    },
    { skip: !epi?.id }
  );

  const { data: { vinculos } = {} } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'listaJson2',
    contain: ['Usuarios'],
    sort: 'Usuarios.nome',
    options: { status: 1 },
  });

  const enviar = async (dados: Epi): Promise<void> => {
    try {
      await editarEpi(dados).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

      navegacao('/epis');
    } catch (error) {
      alerta({
        titulo: 'Erro ao editar',
        conteudo: recuperarMensagemDeErro((error as Erro).data.errors),
        tema: 'danger',
      });
    }
  };

  const enviarEmprestimo = async (dados: Emprestimo): Promise<void> => {
    try {
      await adicionarEmprestimoEpi({ ...dados, epi_id: epi?.id }).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      setModal(false);
    } catch (error) {
      alerta({
        titulo: 'Erro ao Adicionar',
        conteudo: recuperarMensagemDeErro((error as Erro).data.errors),
        tema: 'danger',
      });
    }
  };

  const editarEmprestimo = async (dados: Emprestimo): Promise<void> => {
    try {
      await editarEmprestimoEpi({
        ...dados,
        data_devolucao: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'),
      }).unwrap();

      toast({ conteudo: 'Devolvido com sucesso!', tema: 'success' });
    } catch (error) {
      alerta({
        titulo: 'Erro ao devolver',
        conteudo: recuperarMensagemDeErro((error as Erro).data.errors),
        tema: 'danger',
      });
    }
  };

  const removerEmprestimo = async (id: number): Promise<void> => {
    try {
      await removerEmprestimoEpi({ id }).unwrap();

      toast({ conteudo: 'Removido com sucesso!', tema: 'success' });
    } catch (error) {
      alerta({
        titulo: 'Erro ao Remover',
        conteudo: recuperarMensagemDeErro((error as Erro).data.errors),
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Editando EPI"
        descricao="Altere o formulário e clique em enviar para editar o EPI"
      />
      <div className="row justify-content-center">
        <div className="col-md-4">
          <div className="bg-white shadow-sm rounded p-4">
            <Form
              onSubmit={enviar}
              inicial={epi}
              desabilitado={isLoading}
              regras={regras}
            >
              <Campo name="nome" rotulo="Nome" />
              <Campo
                name="certificado_de_aprovacao"
                rotulo="Certificado de Aprovação"
              />
              <div className="mt-5">
                <BotaoForm>Enviar</BotaoForm>
                <BotaoLink tema="light" to="/epis" className="ms-2">
                  Cancelar
                </BotaoLink>
              </div>
            </Form>
          </div>
        </div>
        <div className="col-md-8">
          <Botao onClick={() => setModal(true)} tema="success" className="mb-3">
            Realizar emprestimo
          </Botao>
          <div className="bg-white shadow-sm rounded">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Funcionário</th>
                  <th>Quantidade</th>
                  <th>Assinatura</th>
                  <th>Data</th>
                  <th>Devolução</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {emprestimos?.map((emprestimo) => (
                  <tr key={`${emprestimo.id}-${emprestimo.modificado}`}>
                    <td valign="middle">{emprestimo.id}</td>
                    <td valign="middle">
                      {emprestimo.vinculo_empregaticio?.usuario?.nome}
                    </td>
                    <td valign="middle">{emprestimo.quantidade}</td>
                    <td valign="middle">
                      <Imagem
                        src={emprestimo.assinatura}
                        alt="Assinatura"
                        height={50}
                      />
                    </td>
                    <td valign="middle">
                      {emprestimo.criado
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </td>
                    <td valign="middle">
                      {emprestimo?.data_devolucao
                        ?.data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </td>
                    <td valign="middle" align="right">
                      {!emprestimo.data_devolucao && (
                        <Botao
                          sm
                          icone="arrow-return-left"
                          onClick={() => editarEmprestimo(emprestimo)}
                          confirmacao="Deseja devolver esse emprestimo?"
                          tema="success"
                        />
                      )}{' '}
                      <Botao
                        sm
                        icone="trash"
                        onClick={() => removerEmprestimo(emprestimo.id)}
                        confirmacao="Deseja remover esse emprestimo?"
                        tema="danger"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal titulo="Emprestimos de EPI" exibir={modal} aoFechar={setModal}>
        <Form onSubmit={enviarEmprestimo} regras={regrasEmprestimo}>
          <Selecao name="vinculo_id" rotulo="Funcionário" opcoes={vinculos} />
          <Campo name="quantidade" rotulo="Quantidade" />
          <BotaoForm>Enviar</BotaoForm>
        </Form>
      </Modal>
    </LayoutDefault>
  );
};

export default Editar;
